import axios from 'axios';

const axiosInstance = axios.create({
	withCredentials: true,
});

axiosInstance.defaults.baseURL =
	import.meta.env.VITE_WEBCAU_MODE === 'dev'
		? import.meta.env.VITE_NGINX_URL_DEV
		: import.meta.env.VITE_NGINX_URL_PROD;

let accessToken: string | null = null;

let isRefreshing = false;
let failedQueue: Array<{
	resolve: (token: string) => void;
	reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null) => {
	failedQueue.forEach(({ resolve, reject }) => {
		if (error) {
			reject(error);
		} else {
			resolve(token!);
		}
	});
	failedQueue = [];
};

export const setAccessToken = (token: string | null) => {
	accessToken = token;
};

export const getAccessToken = () => accessToken;

axiosInstance.interceptors.request.use(
	(config) => {
		if (accessToken) {
			config.headers.Authorization = `Bearer ${accessToken}`;
		}
		return config;
	},
	(error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
	(response) => response,
	async (error) => {
		const originalRequest = error.config;

		// Prevent infinite loops: check if we already retried or if the failed request was the refresh or login endpoint
		// (login failures are EXPECTED 401s — the login form surfaces them itself;
		// the auth-instance must not treat them as expired sessions and hard-reload).
		if (
			error.response?.status === 401 &&
			!originalRequest._retry &&
			originalRequest.url !== '/api/refresh-token' &&
			(originalRequest.url ?? '').endsWith('/api/login') === false
		) {
			// If a refresh is already in flight, queue this request to use the new token
			if (isRefreshing) {
				return new Promise<string>((resolve, reject) => {
					failedQueue.push({ resolve, reject });
				}).then((token) => {
					originalRequest.headers.Authorization = `Bearer ${token}`;
					return axiosInstance(originalRequest);
				});
			}

			originalRequest._retry = true;
			isRefreshing = true;

			try {
				const response = await axiosInstance.post('/api/refresh-token');
				const newAccessToken = response.data.accessToken;
				setAccessToken(newAccessToken);
				processQueue(null, newAccessToken);

				// Update header for the retry
				originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
				return axiosInstance(originalRequest);
			} catch (refreshError) {
				processQueue(refreshError, null);
				// Refresh failed (token expired/revoked)
				setAccessToken(null);
				// Construct the redirect URL with the current path
				const redirectUrl = `/login?redirect=${encodeURIComponent(
					window.location.pathname + window.location.search
				)}`;
				window.location.href = redirectUrl;
				return Promise.reject(refreshError);
			} finally {
				isRefreshing = false;
			}
		}

		return Promise.reject(error);
	}
);

export { axiosInstance };
